import { ContentPollingService } from './ContentPollingService';
import type { IContentDiscoveryService } from './types';

/**
 * Escolhe entre stream aberto e consulta periódica.
 *
 * Os dois entregam a mesma coisa — avisar que o conteúdo mudou — e expõem a
 * mesma superfície. O que muda é o custo no servidor, e é isso que decide qual
 * usar em cada frota.
 *
 * ## Quando trocar
 *
 * Medido em produção em 2026-08-08:
 *
 * | frota | SSE | consulta |
 * |---|---|---|
 * | até ~9.000 telas | cobertura 99,8%, p95 503 ms | funciona, latência maior |
 * | 10.000 telas | **cobertura 87,8%, p95 12,2 s** | cobertura 100%, p95 660 ms |
 * | 20.000 telas | não conecta — 2× o teto de 10.000 do edge | 36% do teto de RPS |
 *
 * O SSE é melhor **enquanto cabe**: entrega em meio segundo e não gasta
 * requisição. Acima de ~9.000 telas ele para de caber, e aí a consulta é a
 * única que atende.
 *
 * ## Quem escolhe
 *
 * O servidor, a cada sync, pelo campo `discovery_mode` — ver
 * `SyncService.applyDiscoveryMode`. Ele conta as telas do tenant e aplica
 * histerese (liga a consulta em 9.000, só volta ao stream abaixo de 8.500 —
 * Renato 2026-08-25, Fase 1; a frota migra 100% de uma vez, sem modo misto).
 *
 * `VITE_CONTENT_DISCOVERY` decide apenas o que a tela faz **antes do primeiro
 * sync**. O build manda `polling` (ver `scripts/generate-env.ts`) porque errar
 * para esse lado custa uma reconexão numa frota pequena, enquanto errar para o
 * outro é uma frota grande inteira abrindo conexão no boot e estourando o teto
 * do edge.
 */

export type ContentDiscoveryMode = 'sse' | 'polling';

export interface CreateContentServiceOptions {
  /** Base do servidor de conteúdo. */
  apiUrl: string;
  deviceId?: string;
  /** Tenant da tela — chave de cache no endpoint de versão. */
  tenantId?: string;
  /** Sobrescreve a variável de ambiente. Útil em teste. */
  mode?: ContentDiscoveryMode;
}

export function resolveDiscoveryMode(
  override?: ContentDiscoveryMode,
): ContentDiscoveryMode {
  if (override) return override;
  const fromEnv = (import.meta as { env?: Record<string, string> }).env
    ?.VITE_CONTENT_DISCOVERY;
  // Só 'sse' explícito volta ao stream. Ausente cai na consulta: um build que
  // pule o generate-env.ts (já aconteceu — ver o incidente do install-tv.sh em
  // .claude/rules/avplay-sempre-on.md) não pode colocar uma frota grande
  // inteira abrindo conexão no boot.
  return fromEnv === 'sse' ? 'sse' : 'polling';
}

/**
 * Devolve o serviço de descoberta de conteúdo discreto (consulta periódica).
 *
 * Antes esta função escolhia entre um stream SSE próprio e a consulta. O stream
 * discreto (`ContentSSEService`, a 2ª conexão) foi removido com a unificação do
 * SSE: em modo SSE o conteúdo passou a chegar pelo canal UNIFICADO (RemoteOps),
 * e o `SyncService` nem chama esta função nesse modo — o gate
 * `SyncService.shouldUseUnifiedContent` intercepta antes. Hoje só o modo de
 * consulta chega aqui, então só resta o `ContentPollingService`.
 *
 * O tipo de retorno é `IContentDiscoveryService` porque o `SyncService` depende
 * de dois métodos fora da `IContentSSEService` — `ensureConnected` e
 * `updateDeviceId` —, que o `ContentPollingService` implementa.
 */
export function createContentService(
  options: CreateContentServiceOptions,
): IContentDiscoveryService {
  return new ContentPollingService({
    baseUrl: options.apiUrl,
    deviceId: options.deviceId,
    tenantId: options.tenantId,
  });
}
